import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FUNNELS } from "@/lib/funnels";
import { useSeo } from "@/lib/seo";
import { trackFunnel } from "@/lib/tracking";
import { SuccessStories } from "@/components/funnel/SuccessStories";
import { LogoWall } from "@/components/funnel/LogoWall";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { QuizFunnel } from "@/components/funnel/QuizFunnel";
import { PathCards } from "@/components/funnel/PathCards";
import { AnimatedHeadline } from "@/components/AnimatedHeadline";
import { ArrowRight, CheckCircle2, PhoneCall, FileDown, Sparkles } from "lucide-react";

const scrollToQuiz = () => document.getElementById("funnel-quiz")?.scrollIntoView({ behavior: "smooth", block: "start" });

const Hero = ({ seg, cfg, t }) => (
  <section className={`relative overflow-hidden bg-gradient-to-br ${cfg.tint}`}>
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 py-14 lg:grid-cols-[1.15fr_1fr] lg:py-20">
      <div>
        <span className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${cfg.accent} px-4 py-2 text-sm font-bold text-white shadow-lg sm:text-base`} data-testid="funnel-badge">
          <Sparkles className="h-4 w-4" /> {t(`funnel.${seg}.badge`)}
        </span>
        <AnimatedHeadline line1={t(`funnel.${seg}.hook`)} line2={t(`funnel.${seg}.hookAccent`)} size="text-4xl sm:text-5xl lg:text-6xl" accent={cfg.accentText} />
        <p className="mt-6 max-w-2xl text-xl font-medium leading-relaxed text-slate-700" data-testid="funnel-sub">{t(`funnel.${seg}.sub`)}</p>
        <Button onClick={scrollToQuiz} size="lg" className={`mt-8 h-14 rounded-full bg-gradient-to-r ${cfg.accent} px-9 text-lg font-bold text-white shadow-xl transition-transform hover:scale-[1.03]`} data-testid="funnel-hero-cta">
          {t("funnel.common.start")} <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <p className="mt-3 text-sm font-semibold text-slate-600">{t("funnel.common.takes")}</p>
      </div>
      <div className="relative">
        <img src={cfg.image} alt={t(`funnel.${seg}.badge`)} className="w-full rounded-3xl object-cover shadow-2xl aspect-[4/3]" />
        <OfferCard seg={seg} t={t} className="absolute -bottom-8 left-4 right-4 sm:left-auto sm:-left-6 sm:right-auto sm:max-w-sm" />
      </div>
    </div>
  </section>
);

const OfferCard = ({ seg, t, className = "" }) => (
  <div className={`rounded-2xl border border-white bg-white/95 p-5 shadow-xl backdrop-blur ${className}`} data-testid="funnel-offer">
    <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">{t("funnel.common.freeOffer")}</p>
    <p className="mt-1 font-heading text-lg font-extrabold leading-snug text-slate-900">{t(`funnel.${seg}.offerTitle`)}</p>
    <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-800"><PhoneCall className="h-3.5 w-3.5" /> {t("funnel.common.callBadge")}</span>
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-1 text-sky-800"><FileDown className="h-3.5 w-3.5" /> {t("funnel.common.guideBadge")}</span>
    </div>
  </div>
);

const Benefits = ({ seg, cfg, t, tight }) => {
  const items = t(`funnel.${seg}.benefits`, { returnObjects: true });
  const proof = t(`funnel.${seg}.proof`, { returnObjects: true });
  return (
    <section className={`mx-auto max-w-7xl px-4 sm:px-6 pb-16 ${tight ? "pt-12" : "pt-24"}`}>
      <div className="grid gap-4 sm:grid-cols-3" data-testid="funnel-proof">
        {proof.map((p) => (
          <div key={p.l} className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
            <p className={`font-heading text-4xl font-extrabold bg-gradient-to-r ${cfg.accent} bg-clip-text text-transparent`}>{p.v}</p>
            <p className="mt-1 text-sm font-semibold text-slate-600">{p.l}</p>
          </div>
        ))}
      </div>
      <h2 className="mt-16 font-heading text-3xl font-extrabold sm:text-4xl">{t("funnel.common.benefitsTitle")}</h2>
      <div className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="funnel-benefits">
        {items.map((b, i) => (
          <motion.div key={b.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6">
            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
            <div>
              <h3 className="font-heading text-lg font-bold text-slate-900">{b.t}</h3>
              <p className="mt-1 text-base text-slate-600">{b.d}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const Steps = ({ seg, cfg, t }) => {
  const steps = t(`funnel.${seg}.steps`, { returnObjects: true });
  return (
    <section className="bg-slate-900 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <h2 className="font-heading text-3xl font-extrabold sm:text-4xl">{t("funnel.common.howTitle")}</h2>
        <ol className="mt-8 grid gap-5 sm:grid-cols-3" data-testid="funnel-steps">
          {steps.map((s, i) => (
            <li key={s} className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${cfg.accent} font-heading text-lg font-extrabold`}>{i + 1}</span>
              <p className="mt-4 text-lg font-semibold">{s}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default function FunnelPage({ seg }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cfg = FUNNELS[seg];
  useSeo({ title: cfg.seoTitle, description: cfg.seoDesc });
  useEffect(() => {
    trackFunnel(seg, "visit");
  }, [seg]);
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`funnel-page-${seg}`}>
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="funnel-brand">
            <img src="/logo.png" alt="Classroom to Corporate" className="h-10 w-10 object-contain" />
            <span className="font-heading text-lg font-extrabold text-slate-900">Classroom to <span className="text-amber-600">Corporate</span></span>
          </button>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button onClick={scrollToQuiz} className={`hidden rounded-full bg-gradient-to-r ${cfg.accent} font-bold text-white sm:inline-flex`} data-testid="funnel-header-cta">{t("funnel.common.finalCta")}</Button>
          </div>
        </div>
      </header>
      <Hero seg={seg} cfg={cfg} t={t} />
      {seg === "employer" && <LogoWall />}
      <Benefits seg={seg} cfg={cfg} t={t} tight={seg === "employer"} />
      {seg === "student" && <SuccessStories />}
      <Steps seg={seg} cfg={cfg} t={t} />
      <section id="funnel-quiz" className={`scroll-mt-20 bg-gradient-to-br ${cfg.tint}`}>
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16">
          <p className="mb-2 text-center text-sm font-bold uppercase tracking-wider text-emerald-700">{t("funnel.common.freeOffer")}</p>
          <h2 className="mb-3 text-center font-heading text-3xl font-extrabold sm:text-4xl">{t(`funnel.${seg}.offerTitle`)}</h2>
          <p className="mx-auto mb-8 max-w-2xl text-center text-lg text-slate-700">{t(`funnel.${seg}.offerDesc`)}</p>
          <QuizFunnel seg={seg} />
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <h2 className="mb-6 font-heading text-2xl font-extrabold">{t("funnel.common.otherPaths")}</h2>
        <PathCards exclude={seg} />
      </section>
      <WhatsAppButton message={t(`funnel.${seg}.whatsappMsg`)} />
    </div>
  );
}
